import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { SectionHeader, TestimonialCard, CTASection } from '../components/shared';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const tiers = [
    {
      id: 'free',
      name: 'Starter',
      description: 'Perfect for trying out Master Ji',
      monthlyPrice: 0,
      yearlyPrice: 0,
      buttonText: 'Join for Free',
      featured: false,
      features: [
        'Up to 50 customers',
        'Up to 10 orders per month',
        'Basic profile management',
        'Order tracking',
        'Email support'
      ]
    },
    {
      id: 'basic',
      name: 'Pro',
      description: 'For growing tailoring businesses',
      monthlyPrice: 999,
      yearlyPrice: 9999,
      buttonText: 'Get Pro',
      featured: true,
      features: [
        'Up to 500 customers',
        'Up to 100 orders per month',
        'Advanced analytics',
        'Custom branding',
        'Priority support',
        'Customer insights'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'For established businesses',
      monthlyPrice: 1999,
      yearlyPrice: 19999,
      buttonText: 'Get Premium',
      featured: false,
      features: [
        'Up to 5,000 customers',
        'Up to 1,000 orders per month',
        'API access',
        'Custom integrations',
        'Multi-location support',
        'Inventory management',
        'Dedicated account manager'
      ]
    }
  ];

  const testimonials = [
    {
      quote: "Master Ji has transformed how I manage my tailoring business. The Pro plan gives me everything I need.",
      authorName: "Rajesh Kumar",
      authorRole: "Master Tailor, Delhi",
      rating: 5
    },
    {
      quote: "The analytics and customer insights helped me grow my business by 40% in just 3 months.",
      authorName: "Priya Sharma",
      authorRole: "Boutique Owner, Mumbai",
      rating: 5
    },
    {
      quote: "Worth every rupee! The time I save on order management lets me focus on what I love - tailoring.",
      authorName: "Mohammed Ali",
      authorRole: "Fashion House, Hyderabad",
      rating: 5
    }
  ];

  const handleUpgrade = async (tierId: string) => {
    if (!user) {
      navigate('/auth?mode=signup');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/auth?mode=signup');
        return;
      }

      const response = await fetch(`${API_BASE}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tier_id: tierId,
          billing_cycle: billingCycle,
          payment_method: 'razorpay'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const result = await response.json();
      
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (_error) {
      console.error('Upgrade error:', error);
      alert('Failed to start upgrade process. Please try again.');
    }
  };

  const isCurrentTier = (tierId: string) => {
    return user?.tier === tierId;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blue-700 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-brand-yellow/20 text-brand-yellow px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              Pricing
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Choose Your
              <span className="block text-brand-yellow">Plan</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Scale your tailoring business with the right plan for your needs.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-brand-blue shadow-lg'
                    : 'text-white hover:text-brand-yellow'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-brand-blue shadow-lg'
                    : 'text-white hover:text-brand-yellow'
                }`}
              >
                Annually
                <span className="ml-2 text-xs bg-brand-yellow text-brand-blue px-2 py-0.5 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </motion.div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16 fill-white" viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z"></path>
          </svg>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 lg:py-28 bg-white -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-3xl p-8 ${
                  tier.featured
                    ? 'bg-brand-blue text-white shadow-2xl scale-105 z-10'
                    : 'bg-white border-2 border-gray-100 shadow-lg'
                }`}
              >
                {/* Current Plan Badge */}
                {isCurrentTier(tier.id) && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white text-sm font-medium px-4 py-1 rounded-full shadow-lg">
                      Current Plan
                    </span>
                  </div>
                )}

                {/* Featured Badge */}
                {tier.featured && !isCurrentTier(tier.id) && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-yellow text-brand-blue text-sm font-semibold px-4 py-1 rounded-full shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Tier Header */}
                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${tier.featured ? 'text-white' : 'text-gray-900'}`}>
                    {tier.name}
                  </h3>
                  <p className={`text-sm mb-6 ${tier.featured ? 'text-white/80' : 'text-gray-500'}`}>
                    {tier.description}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-2">
                    <span className={`text-5xl font-bold ${tier.featured ? 'text-white' : 'text-gray-900'}`}>
                      {tier.monthlyPrice === 0 ? 'Free' : `₹${billingCycle === 'monthly' 
                        ? tier.monthlyPrice.toLocaleString('en-IN') 
                        : Math.round(tier.yearlyPrice / 12).toLocaleString('en-IN')}`}
                    </span>
                    {tier.monthlyPrice > 0 && (
                      <span className={tier.featured ? 'text-white/70' : 'text-gray-500'}>
                        /month
                      </span>
                    )}
                  </div>
                  {billingCycle === 'yearly' && tier.monthlyPrice > 0 && (
                    <p className={`text-sm ${tier.featured ? 'text-white/60' : 'text-gray-400'}`}>
                      Billed ₹{tier.yearlyPrice.toLocaleString('en-IN')}/year
                    </p>
                  )}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleUpgrade(tier.id)}
                  disabled={isCurrentTier(tier.id)}
                  className={`w-full py-4 rounded-xl font-semibold transition-all duration-200 mb-8 ${
                    isCurrentTier(tier.id)
                      ? 'bg-green-100 text-green-700 cursor-not-allowed'
                      : tier.featured
                        ? 'bg-brand-yellow text-brand-blue hover:bg-brand-yellow-400'
                        : 'bg-brand-blue text-white hover:bg-brand-blue-700'
                  }`}
                >
                  {isCurrentTier(tier.id) ? 'Current Plan' : tier.buttonText}
                </button>

                {/* Features */}
                <div>
                  <h4 className={`text-sm font-semibold mb-4 ${tier.featured ? 'text-white' : 'text-gray-900'}`}>
                    Features
                  </h4>
                  <ul className="space-y-3">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          tier.featured ? 'bg-brand-yellow/20' : 'bg-green-100'
                        }`}>
                          <svg 
                            className={`w-3 h-3 ${tier.featured ? 'text-brand-yellow' : 'text-green-600'}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className={tier.featured ? 'text-white/90' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            label="Testimonials"
            title="Customer Feedbacks"
            subtitle="See what our customers have to say about Master Ji."
            align="center"
          />

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                quote={testimonial.quote}
                authorName={testimonial.authorName}
                authorRole={testimonial.authorRole}
                rating={testimonial.rating}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Ready to Grow Your Business?"
        subtitle="Start with our free plan and upgrade as you grow."
        primaryButtonText="Get Started Free"
        primaryButtonLink="/auth?mode=signup"
        secondaryButtonText="Contact Sales"
        secondaryButtonLink="/contact"
        variant="yellow"
      />
      
      <Footer />
    </div>
  );
};

export default PricingPage;
